import React from 'react';
import { CheckCircle, XCircle, Target, TrendUp, TrendDown, Star, ArrowRight } from '@phosphor-icons/react';
import OutputRenderer from './OutputRenderer';

/**
 * Renders file analysis results in a structured card layout.
 * Handles both structured JSON and raw markdown fallback.
 */
export default function AnalysisResultView({ output, maxHeight = 500 }) {
  // Extract structured data
  const data = typeof output === 'object' ? output : null;
  const structured = data?.structured;
  const rawResponse = data?.response || (typeof output === 'string' ? output : null);

  // If no structured data, fall back to OutputRenderer
  if (!structured) {
    return <OutputRenderer output={rawResponse || output} maxHeight={maxHeight} />;
  }

  const { title, overall_score, overall_grade, verdict, summary, sections = [], strengths = [], improvements = [], recommendations = [] } = structured;

  const verdictColor = verdict === 'PASS' ? '#00E5C0' : verdict === 'FAIL' ? '#FF6B6B' : '#7D8896';

  return (
    <div className="space-y-4" style={{ maxHeight, overflowY: 'auto' }} data-testid="structured-result">
      {/* Header: Score / Grade / Verdict */}
      <div className="flex items-start gap-4">
        {(overall_score !== null && overall_score !== undefined) && (
          <div className="flex flex-col items-center px-4 py-3 rounded-xl bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.12)]" data-testid="overall-score">
            <span className="text-2xl font-bold text-[#00E5C0]">{overall_score}</span>
            <span className="text-[9px] text-[#5C6778] font-semibold uppercase tracking-wider mt-0.5">/ 100</span>
          </div>
        )}
        {overall_grade && (
          <div className="flex flex-col items-center px-4 py-3 rounded-xl bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.12)]" data-testid="overall-grade">
            <span className="text-2xl font-bold text-[#00E5C0]">{overall_grade}</span>
            <span className="text-[9px] text-[#5C6778] font-semibold uppercase tracking-wider mt-0.5">Grade</span>
          </div>
        )}
        {verdict && verdict !== 'N/A' && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border`}
            style={{ borderColor: `${verdictColor}20`, backgroundColor: `${verdictColor}08` }}
            data-testid="verdict-badge">
            {verdict === 'PASS' ? <CheckCircle size={20} weight="fill" style={{ color: verdictColor }} /> : <XCircle size={20} weight="fill" style={{ color: verdictColor }} />}
            <span className="text-sm font-bold" style={{ color: verdictColor }}>{verdict}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          {title && <h3 className="text-sm font-semibold text-white mb-1">{title}</h3>}
          {summary && <p className="text-xs text-[#B0B8C4] leading-relaxed">{summary}</p>}
        </div>
      </div>

      {/* Sections */}
      {sections.length > 0 && (
        <div className="space-y-2">
          {sections.map((sec, i) => (
            <div key={i} className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.015)] overflow-hidden" data-testid={`section-${i}`}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-[rgba(255,255,255,0.04)]">
                <div className="flex items-center gap-2">
                  <ArrowRight size={10} className="text-[#00E5C0]" />
                  <span className="text-xs font-semibold text-white">{sec.heading}</span>
                </div>
                {sec.score !== null && sec.score !== undefined && (
                  <span className="text-xs font-bold text-[#00E5C0] bg-[rgba(0,229,192,0.06)] px-2 py-0.5 rounded-md">{sec.score}</span>
                )}
              </div>
              <div className="px-4 py-3">
                {sec.content && <p className="text-xs text-[#B0B8C4] leading-relaxed">{sec.content}</p>}
                {sec.items && sec.items.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {sec.items.map((item, j) => (
                      <li key={j} className="text-xs text-[#7D8896] flex items-start gap-2">
                        <span className="w-1 h-1 rounded-full bg-[#5C6778] mt-1.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Strengths & Improvements */}
      {(strengths.length > 0 || improvements.length > 0) && (
        <div className="grid grid-cols-2 gap-3">
          {strengths.length > 0 && (
            <div className="rounded-xl border border-[rgba(0,229,192,0.1)] bg-[rgba(0,229,192,0.02)] p-3" data-testid="strengths">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendUp size={12} className="text-[#00E5C0]" />
                <span className="text-[10px] font-semibold text-[#00E5C0] uppercase tracking-wider">Strengths</span>
              </div>
              <ul className="space-y-1">
                {strengths.map((s, i) => (
                  <li key={i} className="text-[11px] text-[#B0B8C4] flex items-start gap-1.5">
                    <Star size={8} weight="fill" className="text-[#00E5C0] mt-1 shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {improvements.length > 0 && (
            <div className="rounded-xl border border-[rgba(245,158,11,0.1)] bg-[rgba(245,158,11,0.02)] p-3" data-testid="improvements">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendDown size={12} className="text-[#F59E0B]" />
                <span className="text-[10px] font-semibold text-[#F59E0B] uppercase tracking-wider">Areas for Improvement</span>
              </div>
              <ul className="space-y-1">
                {improvements.map((s, i) => (
                  <li key={i} className="text-[11px] text-[#B0B8C4] flex items-start gap-1.5">
                    <Target size={8} className="text-[#F59E0B] mt-1 shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.015)] p-3" data-testid="recommendations">
          <span className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider block mb-2">Recommendations</span>
          <ul className="space-y-1.5">
            {recommendations.map((r, i) => (
              <li key={i} className="text-[11px] text-[#B0B8C4] flex items-start gap-2">
                <span className="text-[#00E5C0] font-bold text-[10px] mt-0.5">{i + 1}.</span>
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
